/**
 * Runtime-safe environment variable access for browser bundles.
 * Supports:
 * - window.__APP_ENV__ runtime injection
 * - process.env when available
 */

const ENV_LOG_NS = 'runtime-env';

function safeReadWindowEnv(name) {
  try {
    const w = typeof window !== 'undefined' ? window : undefined;
    const envBag = w && w.__APP_ENV__ && typeof w.__APP_ENV__ === 'object' ? w.__APP_ENV__ : null;
    if (!envBag) {
      return undefined;
    }
    return envBag[name];
  } catch {
    return undefined;
  }
}

function safeReadProcessEnv(name) {
  try {
    const p = typeof process !== 'undefined' ? process : undefined;
    const env = p && p.env && typeof p.env === 'object' ? p.env : null;
    if (!env) {
      return undefined;
    }
    return env[name];
  } catch {
    return undefined;
  }
}

export function getEnvVar(name, defaultValue = undefined) {
  const fromWindow = safeReadWindowEnv(name);
  if (fromWindow != null && String(fromWindow) !== '') {
    return String(fromWindow);
  }
  const fromProcess = safeReadProcessEnv(name);
  if (fromProcess != null && String(fromProcess) !== '') {
    return String(fromProcess);
  }
  return defaultValue;
}

export function getBooleanEnv(name, defaultValue) {
  const raw = getEnvVar(name, undefined);
  if (raw == null) {
    return Boolean(defaultValue);
  }
  const v = String(raw).trim().toLowerCase();
  if (v === 'true' || v === '1' || v === 'yes' || v === 'on') {
    return true;
  }
  if (v === 'false' || v === '0' || v === 'no' || v === 'off') {
    return false;
  }
  return Boolean(defaultValue);
}

export function getNumberEnv(name, defaultValue, min = Number.NEGATIVE_INFINITY) {
  const raw = getEnvVar(name, undefined);
  if (raw == null || raw === '') {
    return defaultValue;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return defaultValue;
  }
  return n < min ? defaultValue : n;
}

export function detectRuntimeEnvironment() {
  const hasWindow = typeof window !== 'undefined';
  const hasDocument = typeof document !== 'undefined';
  const isBrowser = hasWindow && hasDocument;

  const hasWebpackRuntime =
    isBrowser &&
    (typeof window.__webpack_require__ === 'function' ||
      Object.keys(window).some((k) => k.startsWith('webpackChunk')));

  const hasViteRuntime = isBrowser && typeof window.__vite_plugin_react_preamble_installed__ !== 'undefined';

  const nodeEnv = getEnvVar('NODE_ENV', undefined);
  const resolvedMode =
    nodeEnv ||
    (isBrowser && window.location && /localhost|127\.0\.0\.1/i.test(window.location.hostname)
      ? 'development'
      : 'production');

  let bundler = 'unknown';
  if (hasWebpackRuntime) bundler = 'webpack';
  if (hasViteRuntime) bundler = 'vite';
  if (!hasWebpackRuntime && !hasViteRuntime && getEnvVar('REACT_APP_VERSION', undefined) != null) {
    bundler = 'cra-or-webpack';
  }

  return {
    runtimeType: isBrowser ? 'browser' : 'non-browser',
    bundler,
    mode: resolvedMode,
    hasWindowAppEnv: isBrowser && typeof window.__APP_ENV__ === 'object',
    hasProcessEnv: safeReadProcessEnv('NODE_ENV') != null,
  };
}

let realtimeEnvLogged = false;

export function logRealtimeEnvStartup({ featureEnabled, debounceMs }) {
  if (realtimeEnvLogged) {
    return;
  }
  realtimeEnvLogged = true;

  const runtime = detectRuntimeEnvironment();
  const payload = {
    ns: ENV_LOG_NS,
    stage: 'queue_realtime_startup',
    ts: new Date().toISOString(),
    runtimeType: runtime.runtimeType,
    bundler: runtime.bundler,
    mode: runtime.mode,
    hasWindowAppEnv: runtime.hasWindowAppEnv,
    hasProcessEnv: runtime.hasProcessEnv,
    queueRealtimeSyncEnabled: featureEnabled,
    queueRealtimeDebounceMs: debounceMs,
  };
  //console.log(`[${ENV_LOG_NS}] ${JSON.stringify(payload)}`);
}

